/**
 * ``AnnotationHandleLayer`` — paints the edit handles while the Edit tool is
 * active (REQ-UI-135; ADR-034 D7 amended).
 *
 * A single circle layer over one source, styled data-driven off
 * ``handle_kind``, so vertex / midpoint / radius handles differ visually
 * without needing three sources to keep in sync:
 *
 *   - **vertex** — solid, full opacity. A real corner you can move or delete.
 *   - **midpoint** — smaller and semi-transparent, reading as *latent*: it is
 *     not a corner yet, it becomes one when dragged. Painting it identically to
 *     a vertex would make a 3-corner triangle look like a 6-corner hexagon.
 *   - **radius** — the circle's resize grip, at the outer end of the radius
 *     line so that line signposts the affordance.
 *
 * Handles are a **fixed pixel size** (``circle-radius`` in px, not ground
 * metres), so they stay grabbable at every zoom — the same treatment the
 * arrowheads get for the same reason.
 *
 * The layer mounts and unmounts with the tool: no Edit tool, no handles, no
 * source. That keeps the default map clean and means the hit-routing in
 * ``use-annotation-drag`` never has to ask whether a handle press was
 * meaningful.
 *
 * Renders nothing itself (a MapLibre-side effect component), like the sibling
 * annotation render / label layers.
 *
 * Cross-references:
 *   - ADR-034 D7 (editing surface); feature-spec §8 (interaction model).
 *   - @/annotations/annotation-handles (the pure builder this paints).
 */
import { useEffect, useMemo } from "react";
import type { GeoJSONSource, Map as MapLibreMap } from "maplibre-gl";
import type { FeatureCollection, Point } from "geojson";
import { isMapUsable } from "@/map/map-ready";
import { useAnnotationsWithDrag } from "@/annotations/annotation-drag-store";
import { useAnnotationToolStore } from "@/annotations/annotation-tool-store";
import { annotationsToHandleFeatures } from "@/annotations/annotation-handles";

const SOURCE_ID = "meridian-annotation-handle-source";
export const HANDLE_LAYER_ID = "meridian-annotation-handle-layer";

/** Handle radii in screen px. A vertex is the primary grab target; a midpoint
 *  is deliberately smaller so it reads as secondary and does not crowd the two
 *  real corners it sits between. */
const VERTEX_RADIUS_PX = 5;
const MIDPOINT_RADIUS_PX = 3.5;

/** Dark outline so a handle reads against both its own shape's stroke colour
 *  and whatever basemap is underneath. */
const HANDLE_OUTLINE = "#0f1117";

const EMPTY: FeatureCollection<Point> = { type: "FeatureCollection", features: [] };

/** Attach the source + layer on first call; ``setData`` thereafter. */
function attach(map: MapLibreMap, data: FeatureCollection<Point>): void {
  if (!isMapUsable(map)) {
    return;
  }
  const source = map.getSource(SOURCE_ID);
  if (source !== undefined) {
    (source as GeoJSONSource).setData(data);
    return;
  }
  map.addSource(SOURCE_ID, { type: "geojson", data });
  map.addLayer({
    id: HANDLE_LAYER_ID,
    type: "circle",
    source: SOURCE_ID,
    paint: {
      // Fixed pixel size — a handle must stay grabbable at every zoom.
      "circle-radius": [
        "case",
        ["==", ["get", "handle_kind"], "midpoint"],
        MIDPOINT_RADIUS_PX,
        VERTEX_RADIUS_PX,
      ],
      // Handles take the shape's own stroke colour so it is unambiguous which
      // shape a handle belongs to when two shapes overlap.
      "circle-color": ["get", "stroke_color"],
      // A midpoint is latent, not a corner — half opacity says so at a glance.
      "circle-opacity": ["case", ["==", ["get", "handle_kind"], "midpoint"], 0.5, 1],
      "circle-stroke-color": HANDLE_OUTLINE,
      "circle-stroke-width": 1,
    },
  });
}

/** Remove the layer + source. */
function detach(map: MapLibreMap): void {
  if (!isMapUsable(map)) {
    return;
  }
  if (map.getLayer(HANDLE_LAYER_ID) !== undefined) {
    map.removeLayer(HANDLE_LAYER_ID);
  }
  if (map.getSource(SOURCE_ID) !== undefined) {
    map.removeSource(SOURCE_ID);
  }
}

/** Paint the edit handles while the Edit tool is active. Renders null. */
export function AnnotationHandleLayer({ map }: { map: MapLibreMap }): null {
  const data = useAnnotationsWithDrag();
  const activeTool = useAnnotationToolStore((state) => state.activeTool);
  const editing = activeTool === "edit";
  const annotations = useMemo(() => data ?? [], [data]);

  const handles = useMemo(
    () => (editing ? annotationsToHandleFeatures(annotations) : EMPTY),
    [editing, annotations],
  );

  useEffect(() => () => detach(map), [map]);
  useEffect(() => {
    if (!editing) {
      // Tear the layer down rather than feeding it an empty collection, so no
      // Edit tool means no handle layer on the map at all.
      detach(map);
      return;
    }
    attach(map, handles);
  }, [map, editing, handles]);

  return null;
}
